import { ColumnDef, FilterFnOption } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MultiSelectAll } from 'src/components/common/MultiSelect/MultiSelectAll';
import { SeatingInfo } from 'src/components/common/SeatingInfo';
import { ListingNotesDisplay } from 'src/components/Listings/ListingDisplay/ListingNotesDisplay';
import {
  SaleMarketplaceIdDisplay,
  SaleMarketplaceIdDisplayProps,
} from 'src/components/Sales/SaleMarketplaceIdDisplay';
import { TagValueDisplay } from 'src/components/TagsFormBody/TagValueCell';
import {
  COLUMN_DEFAULT_SIZE_BASE,
  COLUMN_DEFAULT_SIZE_BASE_L,
  COLUMN_DEFAULT_SIZE_L,
  COLUMN_DEFAULT_SIZE_LG,
  COLUMN_DEFAULT_SIZE_XL,
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { Content, useContent } from 'src/contexts/ContentContext';
import {
  NO_GROUP_ID,
  TableRow,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';
import { useSellerAccountContext } from 'src/contexts/SellerAccountContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { vars } from 'src/core/themes';
import { Button, Stack, TooltipPopover } from 'src/core/ui';
import { CopyToClipboardV2 } from 'src/core/ui/CopyToClipboard/CopyToClipboardV2';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { useConsigneeAccountDisplayName } from 'src/hooks/api/useConsigneeAccountDisplayName';
import { useOffices } from 'src/hooks/api/useOffices';
import { useColumnUserSetting } from 'src/hooks/useColumnUserSetting';
import { getUserDisplayName, useGetUserInfos } from 'src/hooks/userGetUserInfo';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import {
  FormatOption,
  FormatOption_Seating,
  FormatOptionEntries,
} from 'src/modals/EditTableColumns';
import { CustomColumnName } from 'src/modals/InventoryTableColumns/CustomColumnName';
import { LaunchUploadBarcodes } from 'src/modals/UploadBarcodes/LaunchUploadBarcodes';
import { IconsFill, WarningOutlineIcon } from 'src/svgs/Viagogo';
import { SEATING_SORTING_FN_NAME } from 'src/tables/common/hooks/useSeatingColumnSorting';
import { CustomNumericCell } from 'src/tables/ListingTable/configs/CustomNumericCell';
import { ProceedsCell } from 'src/tables/SalesTable/configs/ProceedsCell';
import { SeatInfoContainer, TableShimmeringDiv } from 'src/tables/Table';
import { DateCell, InHandCell } from 'src/tables/Table/TableCells';
import { createNumberFooter } from 'src/tables/Table/TableCells/FooterCell';
import { evaluateFormula } from 'src/utils/columns/sales/salesCustomColumnUtils';
import { CustomSalesColumn } from 'src/utils/columns/sales/salesCustomColumnUtils.types';
import { ContentId } from 'src/utils/constants/contentId';
import {
  MARKETPLACE_SALE_PAYMENT_STATE_TO_CID,
  SALE_PAYMENT_STATE_TO_CID,
  SALE_TABLE_COLUMN_ID_TO_CONTENT_ID,
  TICKET_TYPE_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { applyNumberFormatting } from 'src/utils/numberFormatter';
import { getPurchaseOrderRelativeUrl } from 'src/utils/purchaseUtils';
import {
  getSaleStatusDisplayContentId,
  getSaleStatusIcon,
} from 'src/utils/saleUtils';
import {
  filterSeatingSectionFn,
  filterSeatRangeFn,
  filterSeatTraitsFn,
  filterStringMultiSelect,
  safeSortDateTime,
  sortGeneric,
  sortMoney,
  sortSeatingSeatsForTable,
} from 'src/utils/tableUtils';
import { getTicketTypeIcon } from 'src/utils/ticketTypeUtils';
import {
  SECTION_TYPE_TO_PRECISION_SETTING,
  SectionType,
} from 'src/utils/types/sectionType';
import {
  Feature,
  ListingNote,
  MarketplaceFulfillmentState,
  MarketplacePaymentState,
  PointOfSalePaymentState,
  PointOfSaleSaleStatus,
  PosClientConfig,
  SaleActionType,
  SaleClient,
  Seating,
  TagsValueType,
  TicketBarcode,
  TicketType,
  UiMoney,
  UserSetting,
} from 'src/WebApiController';

import {
  SalesTableColumnId,
  SalesTableV1ColumnId,
} from '../../../utils/columns/sales/salesColumnUtils.types';
import {
  SaleWithEvent,
  SHIMMERING_DIV_HEIGHT_SINGLE_SALE,
} from '../SalesTable.type';
import { SalesActionsCell } from './SalesActionsCell';

const COST_COLUMN_MIN_SIZE = 75;
const COST_COLUMN_MAX_SIZE = 125;

const SECTION_TYPE = SectionType.Sales;

export const tagColumnDef = (
  tagName: string
): ColumnDef<SaleWithEvent | null> => ({
  id: tagName,
  header: () => (
    <TableHeaderCellDiv align="right">
      <span style={{ overflowX: 'clip', textOverflow: 'ellipsis' }}>
        {tagName}
      </span>
    </TableHeaderCellDiv>
  ),
  enableColumnFilter: false,
  minSize: COLUMN_MIN_SIZE,
  maxSize: COLUMN_MAX_SIZE,
  accessorFn: (data) => {
    const tag = data?.sale?.tags?.find((t) => t.key === tagName);
    if (
      tag?.valueType === TagsValueType.Int ||
      tag?.valueType === TagsValueType.Decimal
    ) {
      return tag ? parseFloat(tag.value) : null;
    }
    return tag?.value;
  },
  cell: function Cell({ getValue, row: { original }, table }) {
    const { value: storedColumnNumberPrecisions = {} } =
      useServerUserSetting<FormatOptionEntries>({
        id: SECTION_TYPE_TO_PRECISION_SETTING[
          table.options.meta?.sectionType ?? SECTION_TYPE
        ],
      });
    const tag = original?.sale?.tags?.find((t) => t.key === tagName);

    if (
      tag?.valueType === TagsValueType.Int ||
      tag?.valueType === TagsValueType.Decimal
    ) {
      const userDefinedPrecision = storedColumnNumberPrecisions[tagName];

      return (
        <CustomNumericCell
          columnId={tagName}
          userDefinedPrecision={userDefinedPrecision}
          value={getValue<number | null>()}
          isLoading={Boolean(!original || !original.sale)}
          shimmeringDivHeight={SHIMMERING_DIV_HEIGHT_SINGLE_SALE}
          sectionType={table.options.meta?.sectionType ?? SECTION_TYPE}
          matchingTag={tag}
          currencyCode={original?.sale?.currency}
        />
      );
    }

    if (!original || !original.sale) {
      return <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />;
    }

    if (!tag) {
      return null;
    }

    return (
      <TableCellDiv align="right">
        <TagValueDisplay tag={tag} displayMode />
      </TableCellDiv>
    );
  },
});

export const customColumnDef = (
  columnId: string,
  formula?: string | null
): ColumnDef<SaleWithEvent | null> => ({
  id: columnId,
  header: ({ table }) => (
    <TableHeaderCellDiv align="right">
      <CustomColumnName
        id={columnId}
        sectionType={table.options.meta?.sectionType ?? SECTION_TYPE}
      />
    </TableHeaderCellDiv>
  ),
  enableColumnFilter: false,
  minSize: COLUMN_MIN_SIZE,
  maxSize: COLUMN_MAX_SIZE,
  accessorFn: (data) => {
    return formula && data
      ? (evaluateFormula(formula, data.sale) ?? null)
      : null;
  },
  cell: function Cell({ getValue, row: { original }, table }) {
    const { value: storedColumnNumberPrecisions = {} } =
      useServerUserSetting<FormatOptionEntries>({
        id: SECTION_TYPE_TO_PRECISION_SETTING[
          table.options.meta?.sectionType ?? SECTION_TYPE
        ],
      });
    const { value: customColumns = [] } = useServerUserSetting<
      CustomSalesColumn[]
    >({
      id: UserSetting.SaleCustomColumns,
    });

    const userDefinedPrecision = storedColumnNumberPrecisions[columnId];

    return (
      <CustomNumericCell
        columnId={columnId}
        userDefinedPrecision={userDefinedPrecision}
        value={getValue<number | null>()}
        isLoading={Boolean(!original || !original.sale)}
        shimmeringDivHeight={SHIMMERING_DIV_HEIGHT_SINGLE_SALE}
        sectionType={table.options.meta?.sectionType ?? SECTION_TYPE}
        matchingCustomColumn={customColumns.find((c) => c.id === columnId)}
        currencyCode={original?.sale?.currency}
      />
    );
  },
});

export const SALE_TABLE_COLUMNS_CONFIG: Record<
  SalesTableV1ColumnId,
  ColumnDef<SaleWithEvent | null>
> = {
  [SalesTableColumnId.Checkbox]: {
    id: SalesTableColumnId.Checkbox,
    header: (props) => {
      return props.table.options.meta?.showSelectAllCheckbox ? (
        <TableCellDiv
          align="left"
          showTooltip={false}
          style={{ paddingLeft: vars.spacing.xxs }}
        >
          <MultiSelectAll displayMode="checkbox-only" />
        </TableCellDiv>
      ) : (
        ''
      );
    },
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_BASE,
    minSize: COLUMN_DEFAULT_SIZE_BASE,
    maxSize: COLUMN_DEFAULT_SIZE_BASE,
    enableSorting: false,
    enableResizing: false,
    cell: function Cell({
      row: { id, original, getIsSelected, depth, getCanSelectSubRows },
      table,
    }) {
      const {
        setShiftKeyRowSelectionState,
        clearShiftKeySelection,
        isNoGroupIdSet,
      } = useMultiSelectionContext();

      if (!original || !original.sale) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      const isSelected = getIsSelected();
      const canSelect = getCanSelectSubRows() || depth === 0;
      const groupId = isNoGroupIdSet() ? NO_GROUP_ID : original.event.virtId;
      return canSelect ? (
        <TableCellDiv
          align="left"
          showTooltip={false}
          style={{ paddingLeft: vars.spacing.sm }}
        >
          <Checkbox
            checked={isSelected}
            onChange={() => {
              const isCheckedPrev = getIsSelected();
              if (!isCheckedPrev) {
                setShiftKeyRowSelectionState(
                  id,
                  table.getRowModel().rows as unknown as TableRow[],
                  groupId
                );
              } else {
                clearShiftKeySelection();
              }
            }}
          />
        </TableCellDiv>
      ) : null;
    },
  },
  [SalesTableColumnId.Seating]: {
    id: SalesTableColumnId.Seating,
    header: function Header({
      table: {
        options: { meta },
      },
      column,
    }) {
      const seatingText = useContent(
        SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.Seating]
      );
      const sectionText = useContent(ContentId.Section);
      const { userDefinedPrecision } = useColumnUserSetting(
        column.id,
        meta?.sectionType || SectionType.Sales
      );

      const isSectionOnly =
        userDefinedPrecision === FormatOption_Seating.Seating_SectionOnly;
      return (
        <TableHeaderCellDiv align="left">
          <span>
            {isSectionOnly ? `${seatingText} (${sectionText})` : seatingText}
          </span>
        </TableHeaderCellDiv>
      );
    },
    enableColumnFilter: true,
    filterFn: filterSeatingSectionFn as FilterFnOption<SaleWithEvent | null>,
    meta: {
      uniqueValueVariant: 'seatingSection',
    },
    size: COLUMN_DEFAULT_SIZE_XL,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) =>
      data
        ? {
            seating: data.sale.seating,
            listingNotes: data.sale.seatTraits,
            isSeatSaver: data.sale.isSeatSaver,
            adCmpFlightId: data.sale.adCmpFlightId,
            status: data.sale.status,
          }
        : null,
    cell: function Cell({
      getValue,
      row: { original },
      table: {
        options: { meta },
      },
      column,
    }) {
      const hasSeatTraitsColumnFeature = useUserHasFeature(
        Feature.SeatTraitsColumn
      );
      const { userDefinedPrecision } = useColumnUserSetting(
        column.id,
        meta?.sectionType || SectionType.Sales
      );

      const value = getValue<{
        seating: Seating;
        listingNotes: ListingNote[];
        isSeatSaver: boolean;
        adCmpFlightId?: number | null;
        status: PointOfSaleSaleStatus;
      } | null>();
      if (value == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      const { seating, listingNotes, isSeatSaver, adCmpFlightId, status } =
        value;
      const adjustedSeating =
        userDefinedPrecision === FormatOption_Seating.Seating_SectionOnly
          ? {
              ...seating,
              row: null,
              seatFr: null,
              seatTo: null,
            }
          : seating;
      const hasMarketplaceProblem =
        original?.sale?.hasMarketplaceProblem ?? false;
      return (
        <TableCellDiv align="left">
          <Stack justifyContent="spaceBetween" width="full">
            <SeatInfoContainer style={{ maxWidth: '100%', width: '100%' }}>
              <SeatingInfo
                {...adjustedSeating}
                isSeatSaver={isSeatSaver}
                adCampaignFlightId={adCmpFlightId}
                justifyIconContent="spaceBetween"
                isWasted={status === PointOfSaleSaleStatus.Wasted}
              />
              {Boolean(!hasSeatTraitsColumnFeature) && (
                <ListingNotesDisplay seatTraits={listingNotes} />
              )}
            </SeatInfoContainer>
            {hasMarketplaceProblem && (
              <div>
                <TooltipPopover
                  variant="link"
                  triggerContent={
                    <WarningOutlineIcon
                      fill={IconsFill.textWarning}
                      withHoverEffect
                    />
                  }
                >
                  <WarningMessage
                    message={
                      <Content
                        id={ContentId.SaleHasMarketplaceProblemHoverText}
                      />
                    }
                  />
                </TooltipPopover>
              </div>
            )}
          </Stack>
        </TableCellDiv>
      );
    },
    sortingFn: SEATING_SORTING_FN_NAME,
  },
  [SalesTableColumnId.SeatTraits]: {
    id: SalesTableColumnId.SeatTraits,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    enableColumnFilter: true,
    filterFn: filterSeatTraitsFn as FilterFnOption<SaleWithEvent | null>,
    meta: {
      uniqueValueVariant: 'seatTraits',
    },
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.sale?.seatTraits,
    cell: function Cell({ getValue, row: { original } }) {
      const seatTraits = getValue<ListingNote[] | null | undefined>();

      if (original == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return (
        <TableCellDiv align="right">
          <ListingNotesDisplay seatTraits={seatTraits} />
        </TableCellDiv>
      );
    },
  },
  [SalesTableColumnId.QuantitySold]: {
    id: SalesTableColumnId.QuantitySold,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="right">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) => (data ? data.sale.qtySold || 0 : null),
    cell: function Cell({ getValue, column: { id: columnId }, table }) {
      const value = getValue<number>();
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType ?? SECTION_TYPE
      );

      if (value == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return (
        <TableCellDiv align="right">
          {applyNumberFormatting({
            precision: userDefinedPrecision,
            inputNumber: value,
          })}
        </TableCellDiv>
      );
    },
    footer: createNumberFooter(SHIMMERING_DIV_HEIGHT_SINGLE_SALE, SECTION_TYPE),
  },
  [SalesTableColumnId.OrderDate]: {
    id: SalesTableColumnId.OrderDate,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_L,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.sale?.saleDate,
    cell: function Cell({ getValue, column: { id: columnId }, table }) {
      const value = getValue<string>();
      const { userDefinedPrecision = FormatOption.DateTime_MonthDD_Time } =
        useColumnUserSetting(
          columnId,
          table.options.meta?.sectionType ?? SECTION_TYPE
        );

      if (value == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return (
        <TableCellDiv align="left">
          <DateCell
            precision={userDefinedPrecision}
            date={value}
            align="left"
            enableUtcFallback
            hideRelativeTerms
          />
        </TableCellDiv>
      );
    },
    sortingFn: safeSortDateTime<SaleWithEvent>,
  },
  [SalesTableColumnId.InHandDate]: {
    id: SalesTableColumnId.InHandDate,
    header: () => <TableHeaderCellDiv align="left">IH</TableHeaderCellDiv>,
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_BASE_L,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.sale.inHandAt,
    cell: ({ getValue, row: { original } }) => {
      const value = getValue<string | null>();
      if (!original?.sale) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }
      return (
        <TableCellDiv align="left">
          <InHandCell
            date={value ? new Date(value) : null}
            needsToFulfill={Boolean(
              original?.sale.status &&
              ![
                PointOfSaleSaleStatus.ProcessingFulfillment,
                PointOfSaleSaleStatus.Fulfilled,
                PointOfSaleSaleStatus.CancelledHold,
                PointOfSaleSaleStatus.Rejected,
                PointOfSaleSaleStatus.PendingRejection,
              ].includes(original?.sale.status)
            )}
            handleFulfillmentState
          />
        </TableCellDiv>
      );
    },
    sortingFn: safeSortDateTime<SaleWithEvent>,
  },
  [SalesTableColumnId.Status]: {
    id: SalesTableColumnId.Status,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) => (data ? data.sale.status : null),
    cell: function Cell({ getValue, column: { id: columnId }, row, table }) {
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType ?? SECTION_TYPE
      );

      const sale = row.original?.sale;
      const value = getValue() as PointOfSaleSaleStatus;

      const statusContent = useContent(
        getSaleStatusDisplayContentId(value, sale?.mkpContrState, sale?.mkp)
      );

      if (value == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      const SaleStatusIcon = getSaleStatusIcon(
        value,
        sale?.mkpContrState,
        sale?.mkp
      );

      return (
        <TableCellDiv align="left">
          <Stack direction="row" gap="m" alignItems="center">
            {userDefinedPrecision === FormatOption.EnumDisplay_Icon ? (
              <SaleStatusIcon fill={'transparent'} title={statusContent} />
            ) : (
              <div>{statusContent}</div>
            )}

            {
              // if the Sale is fulfilled in both POS and Marketplace, but the version is mismatched, show the warning
              value === PointOfSaleSaleStatus.Fulfilled &&
              sale?.isFulfillVerMis ? (
                <Stack>
                  <TooltipPopover
                    variant="link"
                    triggerContent={
                      <WarningOutlineIcon
                        fill={IconsFill.textWarning}
                        withHoverEffect
                      />
                    }
                  >
                    <WarningMessage
                      message={
                        <Content id={ContentId.VersionMismatchExplanation} />
                      }
                    />
                  </TooltipPopover>
                </Stack>
              ) : value !== PointOfSaleSaleStatus.Fulfilled &&
                sale?.mkpFulfState === MarketplaceFulfillmentState.Fulfilled ? (
                <Stack>
                  <TooltipPopover
                    variant="link"
                    triggerContent={
                      <WarningOutlineIcon
                        fill={IconsFill.textWarning}
                        withHoverEffect
                      />
                    }
                  >
                    <WarningMessage
                      message={
                        <Content id={ContentId.SaleIsFulfilledOnMarketplace} />
                      }
                    />
                  </TooltipPopover>
                </Stack>
              ) : undefined
            }
          </Stack>
        </TableCellDiv>
      );
    },
  },
  [SalesTableColumnId.TicketType]: {
    id: SalesTableColumnId.TicketType,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) => (data ? data.sale.ticketType : null),
    cell: function Cell({
      getValue,
      column: { id: columnId },
      table,
      row: { original },
    }) {
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType ?? SECTION_TYPE
      );
      const value = getValue() as TicketType;
      if (value == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      if (value === TicketType.Barcode && original?.sale) {
        const isEditable = original?.sale.actions.some(
          (action) =>
            action === SaleActionType.Fulfill ||
            action === SaleActionType.Refulfill
        );

        return (
          <TableCellDiv align="left" title={value}>
            <LaunchUploadBarcodes
              isReadOnly={!isEditable}
              onUploadBarcodes={(
                clientConfig: PosClientConfig,
                saleId: number,
                barcodes: TicketBarcode[]
              ) =>
                new SaleClient(clientConfig).updateBarcodesForSale(
                  saleId,
                  barcodes
                )
              }
              entityId={original?.sale.id}
              idOnMkp={original?.sale.idOnMkp}
              barcodeIconOnlyMode={true}
              iconOnlyMode={true}
              iconStyle={{ padding: 0 }}
              cancelTo={undefined}
              variant={undefined}
              action={SaleActionType.Refulfill}
              loadingContentId={FormatContentId.LoadingSaleId}
              errorContentId={FormatContentId.CouldNotFindSaleId}
              searchingContentId={FormatContentId.SearchingForSaleId}
              isSaleNoFulfill={original?.sale.isNoFulfill}
            />
          </TableCellDiv>
        );
      }
      const ticketTypeIcon = getTicketTypeIcon(value, IconsFill.currentColor);
      return (
        <TableCellDiv align="left" title={value}>
          {userDefinedPrecision === FormatOption.EnumDisplay_Icon ? (
            <span style={{ color: vars.color.textStrong }}>
              {ticketTypeIcon}
            </span>
          ) : (
            <Content id={TICKET_TYPE_TO_CID[value]} />
          )}
        </TableCellDiv>
      );
    },
  },
  [SalesTableColumnId.Proceeds]: {
    id: SalesTableColumnId.Proceeds,
    enableColumnFilter: false,
    minSize: COST_COLUMN_MIN_SIZE,
    maxSize: COST_COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="right">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return !data
        ? null
        : {
            proceeds: data.sale.ttlNetProcs,
            margin: undefined, // TODO - provide this when the data is actually available
          };
    },
    cell: function Cell({ getValue, column: { id: columnId }, table }) {
      const value = getValue<{
        proceeds: UiMoney | null;
        margin?: number;
      } | null>();
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType ?? SECTION_TYPE
      );

      if (value == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      const displayValue =
        applyNumberFormatting({
          precision: userDefinedPrecision,
          inputNumber: value.proceeds?.amt,
          currencyCode: value.proceeds?.currency,
          currencyDecimalDigits: value.proceeds?.dec,
        }) ?? undefined;

      return (
        <TableCellDiv align="right">
          <ProceedsCell
            displayValue={displayValue as string | undefined}
            margin={value?.margin}
          />
        </TableCellDiv>
      );
    },
    footer: function Cell({ column: { id: columnId, accessorFn }, table }) {
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType ?? SECTION_TYPE
      );

      const original = table.options.meta?.summaryData;

      if (!original || !original.sale) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      const value = accessorFn?.(original, -1) as {
        proceeds: UiMoney | null;
        margin?: number;
      } | null;

      if (!value) {
        return null;
      }

      const displayValue =
        applyNumberFormatting({
          precision: userDefinedPrecision,
          inputNumber: value.proceeds?.amt,
          currencyCode: value.proceeds?.currency,
          currencyDecimalDigits: value.proceeds?.dec,
        }) ?? undefined;

      return (
        <TableCellDiv align="right">
          <ProceedsCell
            displayValue={displayValue as string | undefined}
            margin={value?.margin}
          />
        </TableCellDiv>
      );
    },
    sortingFn: (rowA, rowB, colId) => {
      type ValueType = {
        proceeds: UiMoney | null;
        margin?: number;
      };
      const a = rowA.getValue<ValueType>(colId);
      const b = rowB.getValue<ValueType>(colId);
      if (!a?.proceeds) return b ? -1 : 1;
      if (!b?.proceeds) return 1;

      return a.proceeds.amt < b.proceeds.amt ? -1 : 1;
    },
  },
  [SalesTableColumnId.SoldTicketCost]: {
    id: SalesTableColumnId.SoldTicketCost,
    enableColumnFilter: false,
    minSize: COST_COLUMN_MIN_SIZE,
    maxSize: COST_COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="right">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return data?.sale?.soldTktCost?.amt;
    },
    cell: function Cell({
      getValue,
      row: { original },
      column: { id: columnId },
      table,
    }) {
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType ?? SECTION_TYPE
      );

      return (
        <CustomNumericCell
          columnId={columnId}
          userDefinedPrecision={userDefinedPrecision}
          value={getValue<number | null>()}
          isLoading={Boolean(!original || !original.sale)}
          shimmeringDivHeight={SHIMMERING_DIV_HEIGHT_SINGLE_SALE}
          sectionType={table.options.meta?.sectionType ?? SECTION_TYPE}
          currencyCode={original?.sale?.currency}
        />
      );
    },
    footer: function Cell({ column: { id: columnId, accessorFn }, table }) {
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType ?? SECTION_TYPE
      );

      const original = table.options.meta?.summaryData;

      if (!original || !original.sale) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      const value = accessorFn?.(original, -1) as number | null;

      return (
        <CustomNumericCell
          columnId={columnId}
          userDefinedPrecision={userDefinedPrecision}
          value={value}
          isLoading={Boolean(!original || !original.sale)}
          shimmeringDivHeight={SHIMMERING_DIV_HEIGHT_SINGLE_SALE}
          sectionType={table.options.meta?.sectionType ?? SECTION_TYPE}
          currencyCode={original?.sale?.currency}
        />
      );
    },
  },
  [SalesTableColumnId.PandL]: {
    id: SalesTableColumnId.PandL,
    enableColumnFilter: false,
    minSize: COST_COLUMN_MIN_SIZE,
    maxSize: COST_COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="right">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return data?.sale?.pandL?.amt;
    },
    cell: function Cell({
      getValue,
      row: { original },
      column: { id: columnId },
      table,
    }) {
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType ?? SECTION_TYPE
      );

      return (
        <CustomNumericCell
          columnId={columnId}
          userDefinedPrecision={userDefinedPrecision}
          value={getValue<number | null>()}
          isLoading={Boolean(!original || !original.sale)}
          shimmeringDivHeight={SHIMMERING_DIV_HEIGHT_SINGLE_SALE}
          sectionType={table.options.meta?.sectionType ?? SECTION_TYPE}
          currencyCode={original?.sale?.currency}
        />
      );
    },
    footer: function Cell({ column: { id: columnId, accessorFn }, table }) {
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType ?? SECTION_TYPE
      );

      const original = table.options.meta?.summaryData;

      if (!original || !original.sale) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      const value = accessorFn?.(original, -1) as number | null;

      return (
        <CustomNumericCell
          columnId={columnId}
          userDefinedPrecision={userDefinedPrecision}
          value={value}
          isLoading={Boolean(!original || !original.sale)}
          shimmeringDivHeight={SHIMMERING_DIV_HEIGHT_SINGLE_SALE}
          sectionType={table.options.meta?.sectionType ?? SECTION_TYPE}
          currencyCode={original?.sale?.currency}
        />
      );
    },
  },
  [SalesTableColumnId.Margin]: {
    id: SalesTableColumnId.Margin,
    enableColumnFilter: false,
    minSize: COST_COLUMN_MIN_SIZE,
    maxSize: COST_COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="right">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return data?.sale?.margin;
    },
    cell: function Cell({
      getValue,
      row: { original },
      column: { id: columnId },
      table,
    }) {
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType ?? SECTION_TYPE
      );

      return (
        <CustomNumericCell
          columnId={columnId}
          userDefinedPrecision={userDefinedPrecision}
          value={getValue<number | null>()}
          isLoading={Boolean(!original || !original.sale)}
          shimmeringDivHeight={SHIMMERING_DIV_HEIGHT_SINGLE_SALE}
          sectionType={table.options.meta?.sectionType ?? SECTION_TYPE}
        />
      );
    },
    footer: function Cell({ column: { id: columnId, accessorFn }, table }) {
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType ?? SECTION_TYPE
      );

      const original = table.options.meta?.summaryData;

      if (!original || !original.sale) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      const value = accessorFn?.(original, -1) as number | null;

      return (
        <CustomNumericCell
          columnId={columnId}
          userDefinedPrecision={userDefinedPrecision}
          value={value}
          isLoading={Boolean(!original || !original.sale)}
          shimmeringDivHeight={SHIMMERING_DIV_HEIGHT_SINGLE_SALE}
          sectionType={table.options.meta?.sectionType ?? SECTION_TYPE}
        />
      );
    },
  },
  [SalesTableColumnId.Charges]: {
    id: SalesTableColumnId.Charges,
    enableColumnFilter: false,
    minSize: COST_COLUMN_MIN_SIZE,
    maxSize: COST_COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="right">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return data?.sale?.charges?.amt;
    },
    cell: function Cell({
      getValue,
      row: { original },
      column: { id: columnId },
      table,
    }) {
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType ?? SECTION_TYPE
      );

      return (
        <CustomNumericCell
          columnId={columnId}
          userDefinedPrecision={userDefinedPrecision}
          value={getValue<number | null>()}
          isLoading={Boolean(!original || !original.sale)}
          shimmeringDivHeight={SHIMMERING_DIV_HEIGHT_SINGLE_SALE}
          sectionType={table.options.meta?.sectionType ?? SECTION_TYPE}
          currencyCode={original?.sale?.currency}
        />
      );
    },
    footer: function Cell({ column: { id: columnId, accessorFn }, table }) {
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType ?? SECTION_TYPE
      );

      const original = table.options.meta?.summaryData;

      if (!original || !original.sale) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      const value = accessorFn?.(original, -1) as number | null;

      return (
        <CustomNumericCell
          columnId={columnId}
          userDefinedPrecision={userDefinedPrecision}
          value={value}
          isLoading={Boolean(!original || !original.sale)}
          shimmeringDivHeight={SHIMMERING_DIV_HEIGHT_SINGLE_SALE}
          sectionType={table.options.meta?.sectionType ?? SECTION_TYPE}
          currencyCode={original?.sale?.currency}
        />
      );
    },
  },
  [SalesTableColumnId.Credits]: {
    id: SalesTableColumnId.Credits,
    enableColumnFilter: false,
    minSize: COST_COLUMN_MIN_SIZE,
    maxSize: COST_COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="right">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return data?.sale?.credits?.amt;
    },
    cell: function Cell({
      getValue,
      row: { original },
      column: { id: columnId },
      table,
    }) {
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType ?? SECTION_TYPE
      );

      return (
        <CustomNumericCell
          columnId={columnId}
          userDefinedPrecision={userDefinedPrecision}
          value={getValue<number | null>()}
          isLoading={Boolean(!original || !original.sale)}
          shimmeringDivHeight={SHIMMERING_DIV_HEIGHT_SINGLE_SALE}
          sectionType={table.options.meta?.sectionType ?? SECTION_TYPE}
          currencyCode={original?.sale?.currency}
        />
      );
    },
    footer: function Cell({ column: { id: columnId, accessorFn }, table }) {
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType ?? SECTION_TYPE
      );

      const original = table.options.meta?.summaryData;

      if (!original || !original.sale) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      const value = accessorFn?.(original, -1) as number | null;

      return (
        <CustomNumericCell
          columnId={columnId}
          userDefinedPrecision={userDefinedPrecision}
          value={value}
          isLoading={Boolean(!original || !original.sale)}
          shimmeringDivHeight={SHIMMERING_DIV_HEIGHT_SINGLE_SALE}
          sectionType={table.options.meta?.sectionType ?? SECTION_TYPE}
          currencyCode={original?.sale?.currency}
        />
      );
    },
  },
  [SalesTableColumnId.Sale]: {
    id: SalesTableColumnId.Sale,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="right">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_XL,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) =>
      !data
        ? null
        : {
            marketplaceSaleId: data.sale.idOnMkp,
            saleId: data.sale.id,
            isExternal: Boolean(data.sale.idOnMkp),
            marketplace: data.sale.mkp,
          },
    cell: ({ getValue }) => {
      const value = getValue();
      if (value == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      const props = value as SaleMarketplaceIdDisplayProps;
      return (
        <TableCellDiv align="right" style={{ height: '100%' }}>
          <Stack
            gap="s"
            alignItems="center"
            justifyContent="end"
            width="full"
            height="full"
            style={{ wordBreak: 'break-all' }}
          >
            <CopyToClipboardV2
              textToCopy={props.marketplaceSaleId || props.saleId}
            >
              <SaleMarketplaceIdDisplay {...props} />
            </CopyToClipboardV2>
          </Stack>
        </TableCellDiv>
      );
    },
    // sort by market, then order
    sortingFn: (rowA, rowB, colId) => {
      const a = rowA.getValue(colId) as SaleMarketplaceIdDisplayProps;
      const b = rowB.getValue(colId) as SaleMarketplaceIdDisplayProps;
      if (!a) return b ? -1 : 1;
      if (!b) return 1;

      if (a.marketplace !== b.marketplace) {
        return a.marketplace < b.marketplace ? -1 : 1;
      }

      // Same marketplace, sort by sale id
      return (a.marketplaceSaleId || '') < (b.marketplaceSaleId || '') ? -1 : 1;
    },
  },
  [SalesTableColumnId.Actions]: {
    id: SalesTableColumnId.Actions,
    enableSorting: false,
    enableResizing: true,
    header: function Cell() {
      return null;
    },
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_DEFAULT_SIZE_LG,
    maxSize: COLUMN_MAX_SIZE,
    cell: ({ row: { original }, table }) => {
      const linkOnly =
        table.options.meta?.formatOptionOverrides?.[
          SalesTableColumnId.Actions
        ] === FormatOption.Actions_LinkOnly;

      return !original ? (
        <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
      ) : (
        <SalesActionsCell
          linkOnly={linkOnly}
          saleId={original.sale.id}
          marketplaceSaleId={original.sale.idOnMkp}
          marketplace={original.sale.mkp}
          saleStatus={original.sale.status}
          actions={original.sale.actions}
          ticketType={original.sale.ticketType}
          isNoFulfill={original.sale.isNoFulfill}
        />
      );
    },
  },
  [SalesTableColumnId.FulfillmentDate]: {
    id: SalesTableColumnId.FulfillmentDate,
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return data?.sale?.fulfillDate;
    },
    cell: function Cell({
      getValue,
      column: { id: columnId },
      row: { original },
      table,
    }) {
      const value = getValue<string>();
      const { userDefinedPrecision = FormatOption.DateTime_MonthDD_Time } =
        useColumnUserSetting(
          columnId,
          table.options.meta?.sectionType ?? SECTION_TYPE
        );

      if (original == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return (
        <DateCell
          precision={userDefinedPrecision}
          date={value}
          align="left"
          enableUtcFallback
          hideRelativeTerms
        />
      );
    },
    sortingFn: safeSortDateTime<SaleWithEvent>,
  },
  [SalesTableColumnId.AutoFulfill]: {
    id: SalesTableColumnId.AutoFulfill,
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return data?.sale?.isNoFulfill;
    },
    cell: function Cell({ getValue, row: { original } }) {
      const value = getValue<string>();

      if (original == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return (
        <TableCellDiv align="left">
          {/* AutoFulfill is negative of IsNoFulfill */}
          <Content id={value ? ContentId.No : ContentId.Yes} />
        </TableCellDiv>
      );
    },
  },
  [SalesTableColumnId.PaymentState]: {
    id: SalesTableColumnId.PaymentState,
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return {
        pmtState: data?.sale?.pmtState,
        mkpPmtState: data?.sale?.mkpPmtState,
      };
    },
    cell: function Cell({ getValue }) {
      const value = getValue() as {
        mkpPmtState: MarketplacePaymentState;
        pmtState: PointOfSalePaymentState;
      };
      if (value.mkpPmtState == null && value.pmtState == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return (
        <TableCellDiv align="left">
          {value.mkpPmtState ? (
            <Content
              id={MARKETPLACE_SALE_PAYMENT_STATE_TO_CID[value.mkpPmtState]}
            />
          ) : (
            <Content id={SALE_PAYMENT_STATE_TO_CID[value.pmtState]} />
          )}
        </TableCellDiv>
      );
    },
  },
  [SalesTableColumnId.PaymentReceived]: {
    id: SalesTableColumnId.PaymentReceived,
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return data?.sale ? (data.sale.pmtRcv ?? null) : undefined;
    },
    cell: function Cell({ getValue }) {
      const value = getValue();
      if (value === undefined) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return (
        <TableCellDiv align="left">
          <Content id={value ? ContentId.Yes : ContentId.No} />
        </TableCellDiv>
      );
    },
  },
  [SalesTableColumnId.PaymentReferenceId]: {
    id: SalesTableColumnId.PaymentReferenceId,
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return data?.sale ? (data.sale.pmtRefId ?? null) : undefined;
    },
    cell: function Cell({ getValue }) {
      const value = getValue<string>();
      if (value === undefined) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return <TableCellDiv align="left">{value}</TableCellDiv>;
    },
  },
  [SalesTableColumnId.PurchaseCreatedBy]: {
    id: SalesTableColumnId.PurchaseCreatedBy,
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return data?.sale?.owners?.poInsertBys;
    },
    cell: function Cell({ getValue, row: { original } }) {
      const { allActiveUserInfos } = useSellerAccountContext();

      const sellerUserIds = getValue<string[] | null>();

      const displayNamesFormatted = useMemo(
        () =>
          sellerUserIds
            ? [...new Set(sellerUserIds)]
                .map(
                  (sellerUserId) =>
                    allActiveUserInfos?.[sellerUserId]?.displayName
                )
                .filter((name) => name != null)
                .join(', ')
            : null,
        [allActiveUserInfos, sellerUserIds]
      );

      if (original == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return <TableCellDiv align="left">{displayNamesFormatted}</TableCellDiv>;
    },
  },
  [SalesTableColumnId.PurchasedBy]: {
    id: SalesTableColumnId.PurchasedBy,
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return data?.sale?.owners?.buyerIds;
    },
    cell: function Cell({ getValue, row: { original } }) {
      const { allActiveUserInfos } = useSellerAccountContext();

      const sellerUserIds = getValue<string[] | null>();

      const displayNamesFormatted = useMemo(
        () =>
          sellerUserIds
            ? [...new Set(sellerUserIds)]
                .map(
                  (sellerUserId) =>
                    allActiveUserInfos?.[sellerUserId]?.displayName
                )
                .filter((name) => name != null)
                .join(', ')
            : null,
        [allActiveUserInfos, sellerUserIds]
      );

      if (original == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return <TableCellDiv align="left">{displayNamesFormatted}</TableCellDiv>;
    },
  },
  [SalesTableColumnId.PurchaseDate]: {
    id: SalesTableColumnId.PurchaseDate,
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return data?.sale?.poDate;
    },
    cell: function Cell({ getValue, row: { original }, table }) {
      const value = getValue<string>();
      const { userDefinedPrecision = FormatOption.DateTime_MonthDD_Time } =
        useColumnUserSetting(
          SalesTableColumnId.PurchaseDate,
          table.options.meta?.sectionType ?? SECTION_TYPE
        );

      if (original == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return (
        <TableCellDiv align="left">
          <DateCell
            precision={userDefinedPrecision}
            date={value}
            enableUtcFallback
            hideRelativeTerms
          />
        </TableCellDiv>
      );
    },
    sortingFn: safeSortDateTime<SaleWithEvent>,
  },
  [SalesTableColumnId.VendorOrderId]: {
    id: SalesTableColumnId.VendorOrderId,
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return data?.sale?.poVendOrdId;
    },
    cell: function Cell({ getValue, row: { original } }) {
      const onViewClick = useCallback(async () => {
        if (!original || !original.sale?.poId) {
          return;
        }
      }, [original]);

      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      if (!original?.sale?.poId) {
        return '';
      }

      const externalPoID = getValue<string>();

      return (
        <TableCellDiv
          align={'left'}
          style={{ textTransform: 'unset' }}
          title={externalPoID}
        >
          <Stack
            gap="s"
            alignItems="center"
            justifyContent="end"
            width="full"
            height="full"
            style={{ wordBreak: 'break-all' }}
          >
            <CopyToClipboardV2 textToCopy={externalPoID}>
              <Link to={getPurchaseOrderRelativeUrl(original?.sale?.poId)}>
                <Button
                  variant="link"
                  size="unset"
                  shape="inline"
                  style={{ fontSize: vars.typography.fontSize['sm'] }}
                  onClick={onViewClick}
                >
                  {getValue<string>()}
                </Button>
              </Link>
            </CopyToClipboardV2>
          </Stack>
        </TableCellDiv>
      );
    },
  },
  [SalesTableColumnId.VendorAccountEmail]: {
    id: SalesTableColumnId.VendorAccountEmail,
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return data?.sale?.poVendAccEmail;
    },
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return (
        <TableCellDiv align={'left'} style={{ textTransform: 'unset' }}>
          <div>{getValue<string>()}</div>
        </TableCellDiv>
      );
    },
  },
  [SalesTableColumnId.OriginatedBy]: {
    id: SalesTableColumnId.OriginatedBy,
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return data?.sale?.origBy;
    },
    cell: function Cell({ getValue, row: { original } }) {
      const deactivatedText = useContent(ContentId.Deactivated);
      const insertByUserId = getValue() as string;
      const getUserInfosQuery = useGetUserInfos([insertByUserId]);

      const displayUserName = useMemo(() => {
        const user = insertByUserId
          ? getUserInfosQuery.data?.[insertByUserId]
          : undefined;

        return user ? getUserDisplayName(user, deactivatedText) : '';
      }, [deactivatedText, getUserInfosQuery.data, insertByUserId]);

      if (original == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return <TableCellDiv align="left">{displayUserName}</TableCellDiv>;
    },
  },
  [SalesTableColumnId.Row]: {
    id: SalesTableColumnId.Row,
    enableColumnFilter: true,
    filterFn: filterStringMultiSelect as FilterFnOption<SaleWithEvent | null>,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="right">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return data?.sale?.seating?.row;
    },
    cell: function Cell({ getValue, row: { original } }) {
      const value = getValue<string>();

      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }
      if (!original.sale) return null;

      return <TableCellDiv align="right">{value}</TableCellDiv>;
    },
    sortingFn: sortGeneric,
  },
  [SalesTableColumnId.Seats]: {
    id: SalesTableColumnId.Seats,
    enableColumnFilter: true,
    filterFn: filterSeatRangeFn as FilterFnOption<SaleWithEvent | null>,
    meta: {
      columnFilterVariant: 'seatRange',
    },
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="right">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return data?.sale?.seating;
    },
    cell: function Cell({ row: { original } }) {
      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }
      if (!original.sale) return null;

      const { seatFr, seatTo } = original.sale.seating;

      return (
        <TableCellDiv align="right">
          <SeatingInfo
            section={''}
            row={null}
            seatFr={seatFr}
            seatTo={seatTo}
            showSeatPrefix={false}
            justifyIconContent="spaceBetween"
          />
        </TableCellDiv>
      );
    },
    sortingFn: sortSeatingSeatsForTable,
  },
  [SalesTableColumnId.TotalPaymentsAmount]: {
    id: SalesTableColumnId.TotalPaymentsAmount,
    enableColumnFilter: false,
    minSize: COST_COLUMN_MIN_SIZE,
    maxSize: COST_COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="right">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return data?.sale?.totalPmt;
    },
    cell: function Cell({ getValue, row: { original } }) {
      const totalAmount = getValue<UiMoney | null>();

      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return <TableCellDiv align="right">{totalAmount?.disp}</TableCellDiv>;
    },
    footer: function Cell({ column: { id: columnId, accessorFn }, table }) {
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType ?? SECTION_TYPE
      );

      const original = table.options.meta?.summaryData;

      if (!original || !original.sale) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      const value = accessorFn?.(original, -1) as number | null;

      return (
        <CustomNumericCell
          columnId={columnId}
          userDefinedPrecision={userDefinedPrecision}
          value={value}
          isLoading={Boolean(!original || !original.sale)}
          shimmeringDivHeight={SHIMMERING_DIV_HEIGHT_SINGLE_SALE}
          sectionType={table.options.meta?.sectionType ?? SECTION_TYPE}
          currencyCode={original?.sale?.currency}
        />
      );
    },
    sortingFn: sortMoney,
  },
  [SalesTableColumnId.TotalConvertedPaymentsAmount]: {
    id: SalesTableColumnId.TotalConvertedPaymentsAmount,
    enableColumnFilter: false,
    minSize: COST_COLUMN_MIN_SIZE,
    maxSize: COST_COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="right">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return data?.sale?.totalConvertedPmt;
    },
    cell: function Cell({ getValue, row: { original } }) {
      const totalConvertedAmount = getValue<UiMoney | null>();

      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return (
        <TableCellDiv align="right">{totalConvertedAmount?.disp}</TableCellDiv>
      );
    },
    footer: function Cell({ column: { id: columnId, accessorFn }, table }) {
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType ?? SECTION_TYPE
      );

      const original = table.options.meta?.summaryData;

      if (!original || !original.sale) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      const value = accessorFn?.(original, -1) as number | null;

      return (
        <CustomNumericCell
          columnId={columnId}
          userDefinedPrecision={userDefinedPrecision}
          value={value}
          isLoading={Boolean(!original || !original.sale)}
          shimmeringDivHeight={SHIMMERING_DIV_HEIGHT_SINGLE_SALE}
          sectionType={table.options.meta?.sectionType ?? SECTION_TYPE}
          currencyCode={
            original?.sale?.totalConvertedPmt?.currency ?? undefined
          }
        />
      );
    },
    sortingFn: sortMoney,
  },
  [SalesTableColumnId.WastedQuantity]: {
    id: SalesTableColumnId.WastedQuantity,
    enableColumnFilter: false,
    minSize: COST_COLUMN_MIN_SIZE,
    maxSize: COST_COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="right">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return data?.sale?.qtyWasted;
    },
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      const wastedQty = getValue<number | null>();

      return <TableCellDiv align="right">{wastedQty}</TableCellDiv>;
    },
    footer: function Cell({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;

      if (!original || !original.sale) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      const value = accessorFn?.(original, -1) as number | null;

      return (
        <TableCellDiv align="right">
          <div>{value != null && !isNaN(value) ? value : null}</div>
        </TableCellDiv>
      );
    },
  },

  [SalesTableColumnId.InternalNotes]: {
    id: SalesTableColumnId.InternalNotes,
    enableColumnFilter: false,
    minSize: COST_COLUMN_MIN_SIZE,
    maxSize: COST_COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={
            SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.InternalNotes]
          }
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return data?.sale?.internalNotesPreview;
    },
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      const internalNotesPreview = getValue<string | null>();

      return <TableCellDiv align="left">{internalNotesPreview}</TableCellDiv>;
    },
  },

  [SalesTableColumnId.ConsigneeSellerAccountId]: {
    id: SalesTableColumnId.ConsigneeSellerAccountId,
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      return data?.sale?.accId;
    },
    cell: function Cell({ getValue, row: { original } }) {
      const { accountName } =
        useConsigneeAccountDisplayName(getValue<string | null>());

      if (original == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return <TableCellDiv align="left">{accountName}</TableCellDiv>;
    },
  },

  [SalesTableColumnId.Office]: {
    id: SalesTableColumnId.Office,
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data?.sale?.officeId,
    cell: function Cell({ getValue, row: { original } }) {
      const { getOfficeName } = useOffices();

      if (original == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      const officeId = getValue<number | null>();
      const officeName = officeId != null ? getOfficeName(officeId) : null;

      return <TableCellDiv align="left">{officeName}</TableCellDiv>;
    },
  },
};
